import React, { Fragment, useState } from 'react';
import { toast } from 'react-toastify';
import { useSendInquiryMutation } from '../features/contactApiSlice';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'reactstrap';

import PhilippPhoto from '../assets/partner/Philip.png';
import { SendInquiry } from '../Constants';

const inquiryTopics = [
  'CustomerInquiry_Topic_Formula',
  'CustomerInquiry_Topic_Results',
  'CustomerInquiry_Topic_Goals',
  'CustomerInquiry_Topic_Routine',
];

const Inquiry = () => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sendInquiry] = useSendInquiryMutation();
  const [inquirySending, setInquirySending] = useState(false);

  const { t } = useTranslation();

  const onSendInquiry = async () => {
    const data = {
      message: message.trim(),
    };

    if (validate(data)) {
      try {
        setInquirySending(true);
        const res = await sendInquiry(data).unwrap();

        if (res.message) {
          clear();

          toast.success(t('CustomerInquiry_Success'));
        } else {
          toast.error(t('CustomerInquiry_Error'));
        }
      } catch (err) {
        toast.error(t('CustomerInquiry_Error'));
      } finally {
        setInquirySending(false);
      }
    }
  };

  const validate = (data) => {
    setError('');
    if (!data.message) {
      setError(t('CustomerInquiry_Required'));
      return false;
    }

    return true;
  };

  const clear = () => {
    setError('');
    setMessage('');
  };

  return (
    <Fragment>
      <section className="inquiry-section section-padding pt-0">
        <div className="t-w-full">
          <div className="t-mx-auto t-w-full t-max-w-4xl t-rounded-2xl t-border t-border-g200 t-bg-white t-p-5 t-shadow-sm sm:t-p-7">
            <div className="t-grid t-gap-6 lg:t-grid-cols-[1fr_280px] lg:t-items-start">
              <div>
                <span className="t-inline-flex t-rounded-full t-bg-primary t-px-3 t-py-1 t-text-xs t-font-bold t-uppercase t-tracking-[0.14em] t-text-navy">
                  {t('CustomerInquiry_Eyebrow')}
                </span>
                <h2 className="t-m-0 t-mt-4 t-font-serif t-text-3xl t-leading-tight t-text-navy sm:t-text-4xl">
                  {t('CustomerInquiry_Title')}
                </h2>
                <p className="t-m-0 t-mt-3 t-max-w-2xl t-text-base t-leading-relaxed t-text-navy/70">
                  {t('CustomerInquiry_Description')}
                </p>
              </div>

              <div className="t-rounded-xl t-border t-border-teal-mid t-bg-teal-bg t-p-4">
                <div className="t-flex t-items-center t-gap-3">
                  <img
                    src={PhilippPhoto}
                    alt="Philipp Pfeiffer"
                    className="!t-static !t-inset-auto !t-m-0 !t-h-16 !t-w-16 t-shrink-0 t-rounded-full t-bg-white t-object-cover t-object-top"
                  />
                  <div>
                    <p className="t-m-0 t-text-base t-font-bold t-text-navy">Philipp Pfeiffer</p>
                    <p className="t-m-0 t-mt-1 t-text-sm t-leading-snug t-text-navy/70">
                      {t('CustomerInquiry_PhilippRole')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="t-mt-7 t-grid t-gap-3 sm:t-grid-cols-2">
              {inquiryTopics.map((topic) => (
                <div
                  key={topic}
                  className="t-flex t-items-center t-gap-3 t-rounded-xl t-border t-border-g200 t-bg-surface t-px-4 t-py-3"
                >
                  <span className="t-h-2 t-w-2 t-shrink-0 t-rounded-full t-bg-secondary" />
                  <span className="t-text-sm t-font-semibold t-text-navy">{t(topic)}</span>
                </div>
              ))}
            </div>

            <div className="t-mt-6 t-rounded-2xl t-bg-surface t-p-4 sm:t-p-5">
              <div className="t-grid t-gap-5 lg:t-grid-cols-[240px_1fr]">
                <div>
                  <p className="t-m-0 t-text-sm t-font-bold t-text-secondary">
                    {t('CustomerInquiry_FormHintTitle')}
                  </p>
                  <p className="t-m-0 t-mt-1 t-text-sm t-leading-relaxed t-text-navy/70">
                    {t('CustomerInquiry_FormHintBody')}
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="customer-inquiry-message"
                    className="t-block t-text-sm t-font-bold t-uppercase t-tracking-[0.12em] t-text-navy"
                  >
                    {t('CustomerInquiry_MessageLabel')}
                  </label>
                  <textarea
                    id="customer-inquiry-message"
                    rows={6}
                    className="t-mt-2 t-block t-w-full t-resize-y t-rounded-xl t-border t-border-g200 t-bg-white t-p-4 t-text-base t-leading-relaxed t-text-navy placeholder:t-text-sm placeholder:t-font-normal placeholder:t-text-g500 focus:t-border-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-secondary/10"
                    placeholder={t('CustomerInquiry_MessagePlaceholder')}
                    aria-describedby="customer-inquiry-help"
                    value={message}
                    maxLength={500}
                    onChange={(e) => {
                      setMessage(e.target.value);
                    }}
                  />
                  <div className="t-mt-2 t-flex t-items-start t-justify-between t-gap-3">
                    <p id="customer-inquiry-help" className="t-m-0 t-text-xs t-text-g500">
                      {t('CustomerInquiry_MessageHelp')}
                    </p>
                    <span className="t-shrink-0 t-text-xs t-text-g500">{message.length}/500</span>
                  </div>
                  {error && <div className="t-mt-2 t-text-sm t-text-red-600">{error}</div>}
                  <div className="t-mt-5">
                    <button
                      className="t-inline-flex t-min-h-[48px] t-items-center t-justify-center t-rounded-full t-bg-primary t-px-8 t-py-3 t-text-base t-font-bold t-text-navy t-transition hover:t-bg-gold disabled:t-cursor-not-allowed disabled:t-opacity-70"
                      onClick={onSendInquiry}
                      disabled={inquirySending}
                    >
                      {inquirySending && <Spinner className="t-mr-2 t-h-5 t-w-5" />}
                      {t(SendInquiry)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default Inquiry;
